import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
const we_next_logo = '/assets/wenext_logo_dark.png';

export function LandingPrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img
                src={we_next_logo}
                alt="WeNext Logo"
                className="w-[200px] min-w-[200px] cursor-pointer"
                onClick={() => navigate('/')}
              />
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-center">Privacy Policy</CardTitle>
            <p className="text-center text-muted-foreground">Effective Date: October 2025</p>
            <p className="text-center text-muted-foreground">Entity: PhotonX Technologies Pvt. Ltd.</p>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
              <p>
                WeNext.ai ("we", "our", or "us") respects your privacy and is committed to protecting your data.
              </p>
              <p>
                This Privacy Policy explains how we collect, use, store, and protect data obtained through our website, platform, and Meta APIs.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Information We Collect</h2>
              <p><strong>From Users:</strong> Business name, email address, phone number, and login details.</p>
              <p><strong>From Meta APIs:</strong> WhatsApp Business Account IDs, Phone Number IDs, Template metadata, and Message delivery reports.</p>
              <p>
                We do not collect or store personal Facebook profile data such as friends, gender, or birthday.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. How We Use This Information</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>To enable WhatsApp Business onboarding via Meta Embedded Signup Flow.</li>
                <li>To sync templates and manage message delivery via WhatsApp Cloud API.</li>
                <li>To provide analytics, campaign tracking, and chat management.</li>
                <li>To send essential service notifications and system updates.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Data Sharing</h2>
              <p>We never sell or share user data with third parties.</p>
              <p>Platform Data from Meta is used only for legitimate business purposes within WeNext.ai.</p>
              <p>
                Service providers (e.g., AWS) may process data strictly under PhotonX's control and contractual data protection terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Data Retention</h2>
              <p>
                We retain business data only as long as the account remains active or as required by law.
              </p>
              <p>
                Users may request deletion of data by contacting <a href="mailto:support@photonxtech.com" className="text-primary hover:underline">support@photonxtech.com</a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Security</h2>
              <p>We employ industry-standard security practices:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Encrypted data transmission (TLS 1.2/1.3)</li>
                <li>Encrypted data storage (AES-256)</li>
                <li>Periodic access audits and restricted personnel access</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. User Rights</h2>
              <p>Users may request:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Access to their stored data</li>
                <li>Correction or deletion of inaccurate data</li>
                <li>Withdrawal of consent at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Compliance</h2>
              <p>WeNext.ai complies with:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Meta Platform Terms and Developer Policies</li>
                <li>WhatsApp Business Policy</li>
                <li>Applicable Indian Data Protection and IT Laws</li>
                <li>GDPR principles for international users</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Updates</h2>
              <p>
                This Privacy Policy may be updated periodically to reflect new regulations or product features. Updates will be posted at this same URL.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Contact</h2>
              <p>For privacy or compliance inquiries:</p>
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <p><strong>Email:</strong> <a href="mailto:privacy@photonxtech.com" className="text-primary hover:underline">privacy@photonxtech.com</a></p>
                <p><strong>Legal:</strong> <a href="mailto:legal@photonxtech.com" className="text-primary hover:underline">legal@photonxtech.com</a></p>
              </div>
              <p className="mt-4">
                <Link to="/terms-of-service" className="text-primary hover:underline">Terms of Service</Link>
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
